import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {Alert, Form, Input, Space} from 'antd';
import {connect} from 'react-redux';
import {StyledRejectExpenseModal} from './StyledRejectExpenseModal';
import {StyledApproveExpenseModalDescription} from '../ApproveExpenseModal/StyledApproveExpenseModal';
import SpinSmall from '../../../../../SpinSmall';
import ApproveExpenseModal from '../ApproveExpenseModal';
import {expensesHelpers, formHelpers} from '../../../../../../utils/helpers';
import {LockIcon} from '../../../../../../icons';

const {Item} = Form;

const defaultFormValues = { comment: '' }

const defaultError = {
  message: null,
  open: false
};

const RejectExpenseModal = ({
  dispatch,
  employeeEmail,

  handleOk,
  handleCancel,
  expense,
  loading = false,
  open,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [form] = Form.useForm();
  const [error, setError] = useState(defaultError);
  const comment = Form.useWatch('comment', {form});

  const trans = (key) => t(`expenses:modal.rejectExpense.${key}`);

  const reviews = useMemo(() => {
    return expensesHelpers.getExpenseApprovalReviews(expense, employeeEmail);
  }, [expense?.approval, employeeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAfterClose = () => {
    form.resetFields();
    if (error.open) setError(defaultError);
  }

  const handleSubmit = (fields) => {
    if (handleOk) {
      handleOk?.({
        fields,
        errorCallback: (response) => {
          const message = response?.data?.message;
          if (message) {
            setError({ ...error, message, open: true });
          } else {
            const fields = formHelpers.getFormServerErrorFields(response);
            form.setFields(fields);
          }
        }
      });
    }
  }

  return (
    <StyledRejectExpenseModal
      afterClose={handleAfterClose}
      cancelButtonProps={{size: 'middle'}}
      forceRender
      okButtonProps={{
        disabled: loading || comment === '',
        size: 'middle',
        type: 'primary',
      }}
      okText={t('reject')}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      open={open}
      title={trans('title')}
      width={400}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Space size='middle' direction='vertical' className='w-100'>
          {reviews.length > 0 && (
            <>
              <Space size='small' direction='vertical' align='center'>
                <StyledApproveExpenseModalDescription>
                  <LockIcon />
                </StyledApproveExpenseModalDescription>
                <StyledApproveExpenseModalDescription>
                  {trans('description')}
                </StyledApproveExpenseModalDescription>
              </Space>
              <ApproveExpenseModal.ReviewsList reviews={reviews} />
            </>
          )}
          <Space size='small' align='center' className='w-100' direction='vertical'>
            <StyledApproveExpenseModalDescription>
              {trans(reviews.length > 0 ? 'description2' : 'descriptionNoReviews')}
            </StyledApproveExpenseModalDescription>
          </Space>
          <Form
            initialValues={defaultFormValues}
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Item
              name='comment'
              rules={[{required: true, message: t('validation.fieldIsRequired')}]}
              required
            >
              <Input.TextArea
                placeholder={trans('placeholder')}
                size='large'
                rows={4}
                style={{ height: 100 }}
              />
            </Item>
          </Form>
        </Space>
        <Collapse isOpened={error.open}>
          <Alert message={error.message} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledRejectExpenseModal>
  );
}

RejectExpenseModal.propTypes = {
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  open: PropTypes.bool.isRequired,
}

const mapStateToProps = ({ user: { employee: { email: employeeEmail } } }) => ({
  employeeEmail,
});

export default connect(mapStateToProps, null)(RejectExpenseModal);
